import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  ShieldQuestion,
  DollarSign,
  Cpu,
} from 'lucide-react';
import { conversationsService } from '@/services/conversations.service';
import type { DocumentExtraction } from '@/services/contact.service';
import { cn } from '@/utils/cn';

const formatValue = (value: unknown): string => {
  if (value == null) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(v => formatValue(v)).join(', ');
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${formatValue(v)}`)
      .join(', ');
  }
  return String(value);
};

interface DocumentsPanelProps {
  conversationId: string;
}

export const DocumentsPanel: React.FC<DocumentsPanelProps> = ({ conversationId }) => {
  const [extractions, setExtractions] = useState<DocumentExtraction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const loadExtractions = useCallback(async () => {
    try {
      const data = await conversationsService.getExtractions(conversationId);
      setExtractions(data);
      setHasData(data.length > 0);
    } catch {
      setHasData(false);
    } finally {
      setIsLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    setIsLoading(true);
    loadExtractions();
  }, [loadExtractions]);

  // Auto-refresh every 15s while panel has data
  useEffect(() => {
    if (!hasData) return;
    const interval = setInterval(loadExtractions, 15000);
    return () => clearInterval(interval);
  }, [hasData, loadExtractions]);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'completed':
        return { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', pill: 'bg-green-100 text-green-700' };
      case 'processing':
        return { icon: Loader2, color: 'text-blue-600', bg: 'bg-blue-100', pill: 'bg-blue-100 text-blue-700' };
      case 'failed':
        return { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-100', pill: 'bg-red-100 text-red-700' };
      default:
        return { icon: Clock, color: 'text-gray-400', bg: 'bg-gray-100', pill: 'bg-gray-100 text-gray-500' };
    }
  };

  const getDocTypeBadge = (docType?: string) => {
    if (!docType) return null;
    const colors: Record<string, string> = {
      passport: 'bg-indigo-100 text-indigo-700',
      id_card: 'bg-blue-100 text-blue-700',
      drivers_license: 'bg-cyan-100 text-cyan-700',
      invoice: 'bg-amber-100 text-amber-700',
      receipt: 'bg-green-100 text-green-700',
      certificate: 'bg-purple-100 text-purple-700',
      contract: 'bg-pink-100 text-pink-700',
      utility_bill: 'bg-orange-100 text-orange-700',
      bank_statement: 'bg-teal-100 text-teal-700',
    };
    return colors[docType] || 'bg-gray-100 text-gray-600';
  };

  const formatCost = (cost?: number) => {
    if (cost == null) return null;
    const num = Number(cost);
    if (num === 0) return null;
    if (num < 0.01) return `$${num.toFixed(4)}`;
    return `$${num.toFixed(2)}`;
  };

  if (isLoading) return (
    <div className="flex items-center justify-center py-8 text-gray-400">
      <Loader2 className="w-5 h-5 animate-spin" />
    </div>
  );
  if (!hasData) return (
    <div className="flex flex-col items-center justify-center py-8 text-gray-400">
      <FileText className="w-8 h-8 mb-2 text-gray-300" />
      <p className="text-xs">No documents yet</p>
    </div>
  );

  return (
    <div className="bg-white">
      {/* Summary bar */}
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
        <p className="text-xs text-gray-500">
          {extractions.filter(e => e.extraction_status === 'completed').length} of {extractions.length} completed
        </p>
      </div>

      {/* Extractions List */}
      <div className="overflow-y-auto">
        {extractions.map((extraction) => {
          const statusConfig = getStatusConfig(extraction.extraction_status);
          const StatusIcon = statusConfig.icon;
          const isExpanded = expandedId === extraction.id;
          const isConfirmed = !!extraction.confirmed_at;
          const extractedEntries = Object.entries(extraction.extracted_data || {});
          const confirmedEntries = Object.entries(extraction.confirmed_data || {});
          const docTypeBadge = getDocTypeBadge(extraction.document_type);
          const cost = formatCost(extraction.cost_usd);

          return (
            <div key={extraction.id} className={cn(
              'border-b-2 border-[#1a1a2e]',
              isExpanded && 'bg-gray-50/50'
            )}>
              {/* Row */}
              <div
                className="flex items-center gap-3 px-5 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => setExpandedId(isExpanded ? null : extraction.id)}
              >
                <div className={cn('flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center', statusConfig.bg)}>
                  <StatusIcon className={cn(
                    'h-4 w-4',
                    statusConfig.color,
                    extraction.extraction_status === 'processing' && 'animate-spin'
                  )} />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {extraction.filename}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {extraction.document_type && docTypeBadge && (
                      <span className={cn('text-[10px] px-1.5 py-0 rounded-full font-medium', docTypeBadge)}>
                        {extraction.document_type.replace(/_/g, ' ')}
                      </span>
                    )}
                    {extraction.confidence_score != null && (
                      <span className="text-[10px] text-gray-400">
                        {(Number(extraction.confidence_score) * 100).toFixed(0)}% conf
                      </span>
                    )}
                    {isConfirmed ? (
                      <ShieldCheck className="w-3 h-3 text-green-500" />
                    ) : extraction.extraction_status === 'completed' ? (
                      <ShieldQuestion className="w-3 h-3 text-amber-400" />
                    ) : null}
                    {cost && (
                      <span className="text-[10px] text-purple-500 font-medium">{cost}</span>
                    )}
                  </div>
                </div>

                <span className={cn('text-xs font-semibold px-2.5 py-1 rounded-full', statusConfig.pill)}>
                  {extraction.extraction_status}
                </span>

                <div className="flex-shrink-0">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-5 pb-4 space-y-3">
                  {/* Error message */}
                  {extraction.error_message && (
                    <div className="bg-red-50 border-2 border-[#1a1a2e] rounded-xl p-3">
                      <p className="text-xs text-red-700">
                        <span className="font-semibold">Error:</span> {extraction.error_message}
                      </p>
                    </div>
                  )}

                  {/* Extracted Fields */}
                  {extractedEntries.length > 0 && (
                    <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
                      <div className="px-3 py-2 bg-gray-50 border-b-2 border-[#1a1a2e]">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Extracted Fields
                        </p>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {extractedEntries.map(([key, value]) => (
                          <div key={key} className="px-3 py-2.5 flex items-start justify-between gap-3">
                            <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{key.replace(/_/g, ' ')}</span>
                            <span className="text-xs text-gray-900 font-medium text-right break-all">
                              {formatValue(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Confirmed Data */}
                  {confirmedEntries.length > 0 && (
                    <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
                      <div className="px-3 py-2 bg-green-50 border-b-2 border-[#1a1a2e] flex items-center gap-2">
                        <ShieldCheck className="w-3.5 h-3.5 text-green-600" />
                        <p className="text-xs font-semibold text-green-700 uppercase tracking-wider">
                          Confirmed Data
                        </p>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {confirmedEntries.map(([key, value]) => (
                          <div key={key} className="px-3 py-2.5 flex items-start justify-between gap-3">
                            <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{key.replace(/_/g, ' ')}</span>
                            <span className="text-xs text-gray-900 font-medium text-right break-all">
                              {formatValue(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Cost & Token Info */}
                  {(cost || extraction.input_tokens || extraction.output_tokens) && (
                    <div className="bg-purple-50 rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
                      <div className="px-3 py-2 bg-purple-100/50 border-b-2 border-[#1a1a2e] flex items-center gap-2">
                        <DollarSign className="w-3.5 h-3.5 text-purple-600" />
                        <p className="text-xs font-semibold text-purple-700 uppercase tracking-wider">
                          Processing Cost
                        </p>
                      </div>
                      <div className="divide-y divide-purple-100">
                        {extraction.model && (
                          <div className="px-3 py-2.5 flex items-center justify-between">
                            <span className="text-xs text-gray-500 font-medium flex items-center gap-1">
                              <Cpu className="w-3 h-3" /> Model
                            </span>
                            <span className="text-xs text-gray-900 font-medium">{extraction.model}</span>
                          </div>
                        )}
                        {extraction.input_tokens != null && (
                          <div className="px-3 py-2.5 flex items-center justify-between">
                            <span className="text-xs text-gray-500 font-medium">Input tokens</span>
                            <span className="text-xs text-gray-900 font-medium">{Number(extraction.input_tokens).toLocaleString()}</span>
                          </div>
                        )}
                        {extraction.output_tokens != null && (
                          <div className="px-3 py-2.5 flex items-center justify-between">
                            <span className="text-xs text-gray-500 font-medium">Output tokens</span>
                            <span className="text-xs text-gray-900 font-medium">{Number(extraction.output_tokens).toLocaleString()}</span>
                          </div>
                        )}
                        {cost && (
                          <div className="px-3 py-2.5 flex items-center justify-between">
                            <span className="text-xs text-gray-500 font-medium">Cost</span>
                            <span className="text-xs text-purple-700 font-semibold">{cost}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Confirmed timestamp */}
                  {isConfirmed && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border-2 border-[#1a1a2e] rounded-xl w-fit">
                      <ShieldCheck className="h-3.5 w-3.5 text-green-600" />
                      <span className="text-xs font-semibold text-green-700">
                        Confirmed {new Date(extraction.confirmed_at!).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
